import { useQueryClient } from '@tanstack/react-query';
import { type JSX, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
  ArtifactRevisionConflictError,
  useSaveArtifactRevision,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import { getCachedArtifactBlob } from './ArtifactPreview.js';
import { textSizeRejection } from './artifact-editing.js';

/** Which version's text the editor asked for. `attempt` makes a re-read of the same number a new request. */
interface LoadRequest {
  revision: number;
  attempt: number;
}

/** The text the editor started from, and the version number it came from. */
interface LoadedText {
  text: string;
  revision: number;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; loaded: LoadedText };

/**
 * What a text artifact with no recorded content type is saved as. The editor only opens for text-shaped
 * files, so `text/plain` is the honest guess — and the type has to be named because storage compares the
 * PUT's type against the signed one.
 */
const FALLBACK_EDIT_CONTENT_TYPE = 'text/plain';

/** Said when a save loses the optimistic lock. Deliberately explicit about the two things it is NOT
 * doing: it did not overwrite the other person's version, and it will not merge the two texts. */
const CONFLICT_TEXT =
  'Someone else saved a new version while you were editing, so nothing here was saved and their version is untouched. Your text is still on screen — copy anything you need, then reload the latest version and edit that. The two texts are not merged.';

/**
 * Edit a text artifact in place and save the result as a NEW version. Only the text-shaped kinds get here
 * ({@link isEditableArtifact} — markdown, csv/tsv, txt/json, html); a binary has no safe in-browser edit,
 * so it stays download-only.
 *
 * The version the editor SAVES AGAINST is the one it loaded, not whatever the head is when the button is
 * clicked. That is the whole optimistic lock: a competing save moves the head, the commit is refused with a
 * 409, and the user decides what to do about it — nothing is merged and nothing is overwritten. So the
 * loaded revision is captured with the text and kept, even when a list refresh hands this component a newer
 * row mid-edit.
 */
export function ArtifactEditor({
  workspaceId,
  artifact,
  onSaved,
  onCancel,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  /** The saved row, so the screen around this editor can show the new version without a reload. */
  onSaved: (updated: WorkspaceArtifact) => void;
  onCancel: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const save = useSaveArtifactRevision(workspaceId);
  const [request, setRequest] = useState<LoadRequest>({
    revision: artifact.headRevision,
    attempt: 0,
  });
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [draft, setDraft] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [conflicted, setConflicted] = useState(false);

  /**
   * Read the requested version's bytes and decode them. Deliberately keyed on `request` and not on
   * `artifact.headRevision`: a head that moves while the user types must NOT reload the box and throw
   * their draft away — that is what the conflict path is for.
   */
  useEffect(() => {
    let cancelled = false;
    setLoad({ status: 'loading' });
    getCachedArtifactBlob(workspaceId, artifact.id, request.revision)
      .then((blob) => blob.text())
      .then((text) => {
        if (cancelled) return;
        setLoad({
          status: 'ready',
          loaded: { text, revision: request.revision },
        });
        setDraft(text);
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, artifact.id, request]);

  const loaded = load.status === 'ready' ? load.loaded : null;
  const hasChanges = loaded !== null && draft !== loaded.text;
  const sizeRejection = hasChanges ? textSizeRejection(draft) : null;

  /** Save the draft as the next version of the artifact, against the version the editor loaded. */
  function saveDraft(): void {
    if (loaded === null || !hasChanges || sizeRejection !== null) return;
    setFailure(null);
    setConflicted(false);
    const contentType = artifact.contentType || FALLBACK_EDIT_CONTENT_TYPE;
    save.mutate(
      {
        id: artifact.id,
        body: new Blob([draft], { type: contentType }),
        contentType,
        baseRevision: loaded.revision,
      },
      {
        onSuccess: onSaved,
        onError: (error) => {
          if (error instanceof ArtifactRevisionConflictError) {
            void queryClient.invalidateQueries({
              queryKey: ['workspace-artifacts', workspaceId],
            });
            setConflicted(true);
            return;
          }
          setFailure(error.message);
        },
      },
    );
  }

  /** Throw the draft away and load the newest version instead — only ever on an explicit click. */
  function reloadLatest(): void {
    setConflicted(false);
    setFailure(null);
    setRequest((previous) => ({
      revision: artifact.headRevision,
      attempt: previous.attempt + 1,
    }));
  }

  if (load.status === 'loading')
    return <Skeleton className="h-64 w-full rounded-lg" />;
  if (load.status === 'error') {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm text-destructive">
          Couldn't load this file for editing. Try again shortly.
        </p>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {conflicted && (
        <div
          role="alert"
          className="flex flex-col items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3"
        >
          <p className="text-sm text-destructive">{CONFLICT_TEXT}</p>
          <Button size="sm" variant="outline" onClick={reloadLatest}>
            Discard my changes and reload
          </Button>
        </div>
      )}
      {sizeRejection !== null && (
        <p className="text-sm text-destructive">{sizeRejection}</p>
      )}
      {failure !== null && (
        <p className="text-sm text-destructive">{failure}</p>
      )}
      <Textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        aria-label={`Edit ${artifact.name}`}
        spellCheck={false}
        className="min-h-64 max-h-[55vh] resize-y overflow-y-auto font-mono text-xs"
      />
      <div className="flex items-center gap-2">
        <p className="text-xs text-muted-foreground">
          {hasChanges
            ? 'Saving adds a new version — the one you opened stays in the history.'
            : 'No changes to save.'}
        </p>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={saveDraft}
            disabled={!hasChanges || sizeRejection !== null || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save new version'}
          </Button>
        </div>
      </div>
    </div>
  );
}
